/**
 * Turning a dry-run's violation list into a verdict on one proposal.
 *
 * Breaches are compared by identity counts rather than by row, because the
 * database rebuilds shift ids on every change and one guard can breach the
 * same rule twice in a day.
 */

import { violationIdentity } from './rosterModel';
import type { FixVerification, RosterRuleViolation, VerificationStatus } from './types';

function countByIdentity(violations: RosterRuleViolation[]): Map<string, number> {
  const counts = new Map<string, number>();
  violations.forEach((violation) => {
    const identity = violationIdentity(violation);
    counts.set(identity, (counts.get(identity) ?? 0) + 1);
  });
  return counts;
}

/** Items of `from` beyond what `other` also holds, identity by identity. */
function surplus(from: RosterRuleViolation[], other: Map<string, number>): RosterRuleViolation[] {
  const allowance = new Map(other);
  return from.filter((violation) => {
    const identity = violationIdentity(violation);
    const left = allowance.get(identity) ?? 0;
    if (left > 0) {
      allowance.set(identity, left - 1);
      return false;
    }
    return true;
  });
}

export function classifyPreview(
  target: RosterRuleViolation | null,
  baseline: RosterRuleViolation[],
  after: RosterRuleViolation[] | null,
  error: string | null,
): FixVerification {
  if (error || !after) {
    return { status: 'failed', resolved: [], introduced: [], remaining: [], error: error ?? 'No preview result' };
  }

  const baselineCounts = countByIdentity(baseline);
  const afterCounts = countByIdentity(after);
  const resolved = surplus(baseline, afterCounts);
  const introduced = surplus(after, baselineCounts);

  let status: VerificationStatus;
  if (target) {
    const identity = violationIdentity(target);
    const targetResolved = (afterCounts.get(identity) ?? 0) < (baselineCounts.get(identity) ?? 0);
    status = !targetResolved ? 'unresolved' : introduced.length > 0 ? 'resolves_with_new' : 'resolves';
  } else {
    status = after.length === 0 ? 'resolves' : introduced.length > 0 ? 'resolves_with_new' : 'unresolved';
  }

  return { status, resolved, introduced, remaining: after, error: null };
}

const STATUS_RANK: Record<VerificationStatus, number> = {
  resolves: 0,
  resolves_with_new: 1,
  unresolved: 2,
  failed: 3,
};

export const verificationRank = (status: VerificationStatus): number => STATUS_RANK[status];

/** True when a proposal is worth offering at all: it clears the breach it was made for. */
export const isOfferable = (verification: FixVerification): boolean => (
  verification.status === 'resolves' || verification.status === 'resolves_with_new'
);

/** Whether a change set clears `violation` too, judged from its preview. */
export function clearsViolation(verification: FixVerification, violation: RosterRuleViolation): boolean {
  const identity = violationIdentity(violation);
  return verification.resolved.some((resolved) => violationIdentity(resolved) === identity);
}
